import React, { FC, useEffect } from 'react';

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Box, Typography } from '@mui/material';
import { FormikProps } from 'formik';

import {
  AdminLegendVariant,
  resetLongTermContractAttributes,
} from '@pharmaplan/common';

import { getZeroHourDate } from '../../../helpers/Functions';
import { ReactComponent as Calendar } from '../../../assets/svg/calendar.svg';
import { Constants, serverDateKey } from '../../../helpers/Constants';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import CustomDatePicker from '../../common/CustomDatePicker';
import { setDialog } from '../../../reducers/dialogReducer';
import CustomTextField from '../../common/CustomTextField';
import DateTimeRange from '../../common/DateTimeRange';
import CustomButton from '../../common/CustomButton';
import strings from '../../../localization';

import ExpressBookingLongTermModal from './ExpressBookingLongTerm/ExpressBookingLongTermModal';
import styles from './styles';

const { timePlaceholder } = Constants;

interface IExpressBookingDuration {
  formik: FormikProps<any>;
  id?: string;
  type: AdminLegendVariant;
  isLongTerm?: boolean;
  openedDate?: string;
  allShiftsLength?: number;
}

const ExpressBookingDuration: FC<IExpressBookingDuration> = ({
  allShiftsLength,
  isLongTerm,
  openedDate,
  formik,
  type,
  id,
}) => {
  const dispatch = useAppDispatch();
  const isEventFromCalendar = !!id;
  const isBooking = type === AdminLegendVariant.booked;

  const selectedShifts = formik.values?.workshiftIds?.length;

  const isFormikError = formik.errors.selectedDate && formik.touched.selectedDate;
  const error = isFormikError ? formik.errors.selectedDate : ' ';

  const shiftLabel = `${selectedShifts}/${allShiftsLength ?? 0}`;

  const serverToday = getZeroHourDate(
    localStorage.getItem(serverDateKey) ?? '',
  );

  const handleNegativeDates = () => {
    formik.setFieldValue('endTime', null);
  };

  const showViewEditContract = async () => {
    dispatch(
      setDialog({
        Component: (
          <ExpressBookingLongTermModal
            type={type}
            isUpdate={isEventFromCalendar}
            formFormik={formik}
            openedDate={openedDate ?? ''}
          />
        ),
        maxWidth: 'lg',
        heading: {
          title: strings.chooseDates,
        },
        showCloseButton: true,
      }),
    );
  };

  useEffect(
    () =>
      () => {
        dispatch(resetLongTermContractAttributes());
      },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return isLongTerm ? (
    <Box>
      <Box sx={[styles.dateContainer, id ? { alignItems: 'flex-end' } : { alignItems: 'center' }]}>
        <CustomTextField
          label={strings.chooseDates}
          disabled
          name="displayDate"
          formik={formik}
          customClass={styles.field}
          EndAdornment={<Calendar />}
          errorContainer={!id}
          id="name"
        />
        <CustomButton
          customButtonStyle={styles.selectButton}
          onClick={showViewEditContract}
          customClass={styles.select}
          label={isBooking || selectedShifts ? strings.formatString(strings.view, '') : strings.select}
        />
      </Box>
      {!!id && !isBooking && (
      <Typography sx={[selectedShifts ? styles.selectedDuration : styles.durationInfo]}>
        {!selectedShifts && <InfoOutlinedIcon sx={{ fontSize: 15, mr: '3px' }} />}
        {`${strings.formatString(strings.datesSelectedFormat, shiftLabel)}.
        ${selectedShifts ? '' : strings.selectedAtleastOneDate}`}
      </Typography>
      )}
    </Box>
  ) : (
    <>
      <CustomDatePicker
        formik={formik}
        minDate={serverToday}
        disabled={isEventFromCalendar}
        label={strings.selectedDate}
        error={error as string}
        name="selectedDate"
        readOnly
      />

      {/* IF BOOKING OR NO ID THEN ENABLE */}
      <Box component="div" sx={styles.timeContainer}>
        <DateTimeRange
          formik={formik}
          disabled={!(isBooking || !id)}
          startName="startTime"
          endName="endTime"
          customStartHandler={handleNegativeDates}
          startPlaceholder={timePlaceholder}
          endPlaceholder={timePlaceholder}
          startNameLabel={strings.startTime}
          endNameLabel={strings.endTime}
          horizontal={false}
          removeMargin
        />
      </Box>
    </>
  );
};
export default ExpressBookingDuration;
